import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import {
  securityMonitorService,
  type SecurityAlertRecord,
  type SecurityAlertRunRecord,
  type SecurityEventFamily,
  type SecurityEventOutcome,
  type SecurityEventRecord,
  type SecurityEventSeverity,
  type SecuritySummary,
} from '../services/securityMonitorService';

const Card = styled.div`
  background: var(--surface);
  border: 1px solid var(--outline);
  border-radius: 14px;
  padding: 1.25rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
`;

const CardTitle = styled.h2`
  margin: 0 0 0.25rem;
  font-size: 1.2rem;
  color: var(--onSurface);
`;

const CardSubtitle = styled.p`
  margin: 0 0 1rem;
  color: var(--onSurfaceVariant);
`;

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const SubCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
  padding: 1rem 1.1rem;
  border-radius: 16px;
  border: 1px solid var(--outline);
  background: color-mix(in srgb, var(--surface) 88%, transparent);
`;

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
`;

const SubTitle = styled.h3`
  margin: 0;
  font-size: 1rem;
  color: var(--onSurface);
`;

const SubTitleDesc = styled.p`
  margin: 0.2rem 0 0;
  color: var(--onSurfaceVariant);
  font-size: 0.9rem;
`;

const Toolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  align-items: center;
  justify-content: space-between;
`;

const ToolbarCluster = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.6rem;
  align-items: center;
`;

const ToolbarMeta = styled.span`
  font-size: 0.85rem;
  color: var(--onSurfaceVariant);
`;

const PrimaryButton = styled.button`
  border: 0;
  border-radius: 999px;
  padding: 0.55rem 0.95rem;
  font-weight: 600;
  cursor: pointer;
  background: var(--primary);
  color: var(--onPrimary);

  &:disabled {
    opacity: 0.65;
    cursor: not-allowed;
  }
`;

const GhostButton = styled.button`
  border: 1px solid var(--outline);
  border-radius: 999px;
  padding: 0.5rem 0.85rem;
  font-weight: 600;
  cursor: pointer;
  background: transparent;
  color: var(--onSurface);
`;

const Select = styled.select`
  min-width: 140px;
  border-radius: 10px;
  border: 1px solid var(--outline);
  background: var(--surface);
  color: var(--onSurface);
  padding: 0.55rem 0.7rem;
`;

const SearchInput = styled.input`
  min-width: 220px;
  border-radius: 10px;
  border: 1px solid var(--outline);
  background: var(--surface);
  color: var(--onSurface);
  padding: 0.55rem 0.7rem;
`;

const SummaryGrid = styled.div`
  display: grid;
  gap: 0.75rem;
  grid-template-columns: repeat(4, minmax(0, 1fr));

  @media (max-width: 1100px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const SummaryTile = styled.div`
  padding: 0.9rem 1rem;
  border-radius: 14px;
  border: 1px solid var(--outline);
  background: color-mix(in srgb, var(--surfaceVariant) 45%, transparent);
`;

const MetricLabel = styled.div`
  font-size: 0.8rem;
  color: var(--onSurfaceVariant);
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const MetricValue = styled.div<{ $bad?: boolean }>`
  margin-top: 0.25rem;
  font-size: 1.4rem;
  font-weight: 700;
  color: ${({ $bad }) => ($bad ? 'var(--error)' : 'var(--onSurface)')};
`;

const SmallText = styled.div`
  color: var(--onSurfaceVariant);
  font-size: 0.82rem;
`;

const EmptyState = styled.div`
  border: 1px dashed var(--outline);
  border-radius: 12px;
  padding: 1rem;
  color: var(--onSurfaceVariant);
  text-align: center;
`;

const Feedback = styled.div<{ $tone: 'info' | 'success' | 'error' }>`
  padding: 0.75rem 0.9rem;
  border-radius: 12px;
  border: 1px solid
    ${({ $tone }) =>
      $tone === 'error'
        ? 'var(--error)'
        : $tone === 'success'
          ? 'var(--success)'
          : 'var(--outline)'};
  color: var(--onSurface);
  background: ${({ $tone }) =>
    $tone === 'error'
      ? 'color-mix(in srgb, var(--error) 14%, transparent)'
      : $tone === 'success'
        ? 'color-mix(in srgb, var(--success) 14%, transparent)'
        : 'color-mix(in srgb, var(--surfaceVariant) 55%, transparent)'};
`;

const TableShell = styled.div`
  border: 1px solid var(--outline);
  border-radius: 12px;
  overflow: hidden;
  background: var(--surface);
`;

const TableScroll = styled.div`
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;

  th,
  td {
    border-bottom: 1px solid var(--outline);
    padding: 0.7rem 0.85rem;
    text-align: left;
    font-size: 0.85rem;
    color: var(--onSurfaceVariant);
    vertical-align: top;
  }

  th {
    background: var(--surfaceVariant);
    text-transform: uppercase;
    letter-spacing: 0.04em;
    font-size: 0.72rem;
  }
`;

const DetailButton = styled.button`
  border: 0;
  background: none;
  color: var(--primary);
  padding: 0;
  cursor: pointer;
  text-align: left;
`;

const Badge = styled.span<{ $tone: 'default' | 'warning' | 'error' | 'critical' | 'success' }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  padding: 0.15rem 0.5rem;
  font-size: 0.74rem;
  font-weight: 700;
  text-transform: uppercase;
  background: ${({ $tone }) =>
    $tone === 'critical'
      ? 'color-mix(in srgb, var(--error) 24%, transparent)'
      : $tone === 'error'
        ? 'color-mix(in srgb, var(--error) 18%, transparent)'
        : $tone === 'warning'
          ? 'color-mix(in srgb, var(--warning) 20%, transparent)'
          : $tone === 'success'
            ? 'color-mix(in srgb, var(--success) 18%, transparent)'
            : 'color-mix(in srgb, var(--surfaceVariant) 80%, transparent)'};
  color: ${({ $tone }) =>
    $tone === 'critical' || $tone === 'error'
      ? 'var(--error)'
      : $tone === 'warning'
        ? 'var(--warning)'
        : $tone === 'success'
          ? 'var(--success)'
          : 'var(--onSurfaceVariant)'};
`;

const PaginationBar = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 0.75rem;
  align-items: center;
  padding: 0.8rem 0.9rem;
  flex-wrap: wrap;
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.5rem;
  z-index: 1000;
`;

const ModalCard = styled.div`
  width: min(820px, 100%);
  max-height: 85vh;
  overflow: auto;
  background: var(--surface);
  border: 1px solid var(--outline);
  border-radius: 16px;
  padding: 1.25rem;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.35);
`;

const DetailGrid = styled.div`
  display: grid;
  gap: 0.75rem;
  grid-template-columns: repeat(2, minmax(0, 1fr));

  @media (max-width: 760px) {
    grid-template-columns: 1fr;
  }
`;

const DetailField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const IdentityStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
`;

const IdentityPrimary = styled.span`
  color: var(--onSurface);
`;

const IdentityMeta = styled.span`
  color: var(--onSurfaceVariant);
  font-size: 0.78rem;
`;

const DetailLabel = styled.span`
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--onSurfaceVariant);
`;

const CodeBlock = styled.pre`
  margin: 0;
  padding: 0.9rem;
  border-radius: 12px;
  background: color-mix(in srgb, var(--surfaceVariant) 75%, transparent);
  overflow: auto;
  white-space: pre-wrap;
  color: var(--onSurface);
  font-size: 0.8rem;
`;

type FeedbackState = {
  tone: 'info' | 'success' | 'error';
  text: string;
} | null;

const compactId = (value?: string | null) => {
  if (!value) return 'n/a';
  return value.length > 12 ? `${value.slice(0, 8)}...${value.slice(-4)}` : value;
};

const normalizeContactValue = (value?: string | null) => {
  const trimmed = value?.trim();
  return trimmed ? trimmed : null;
};

const userPrimaryLabel = (
  user: SecurityEventRecord['actor_user'] | SecurityEventRecord['target_user'],
  userId?: string | null
) => {
  const fullName = normalizeContactValue(user?.full_name);
  if (fullName) return fullName;

  const email = normalizeContactValue(user?.email);
  if (email) return email;

  const phone = normalizeContactValue(user?.phone);
  if (phone) return phone;

  return compactId(userId);
};

const userSecondaryContacts = (
  user: SecurityEventRecord['actor_user'] | SecurityEventRecord['target_user'],
  userId?: string | null
) => {
  const primary = userPrimaryLabel(user, userId);
  return [normalizeContactValue(user?.email), normalizeContactValue(user?.phone)].filter(
    (value, index, items): value is string => Boolean(value) && value !== primary && items.indexOf(value) === index
  );
};

const renderUserSummary = (
  user: SecurityEventRecord['actor_user'] | SecurityEventRecord['target_user'],
  userId?: string | null
) => {
  const primary = userPrimaryLabel(user, userId);
  const secondary = userSecondaryContacts(user, userId);

  return (
    <IdentityStack>
      <IdentityPrimary>{primary}</IdentityPrimary>
      {secondary.length > 0 ? <IdentityMeta>{secondary.join(' • ')}</IdentityMeta> : null}
    </IdentityStack>
  );
};

const renderActorSummary = (event: SecurityEventRecord) => {
  if (event.actor_user || event.actor_user_id) {
    return renderUserSummary(event.actor_user, event.actor_user_id);
  }

  const masked = normalizeContactValue(event.identifier_masked);
  if (masked) {
    return (
      <IdentityStack>
        <IdentityPrimary>{masked}</IdentityPrimary>
        <IdentityMeta>attempted identifier</IdentityMeta>
      </IdentityStack>
    );
  }

  return renderUserSummary(null, null);
};

const userDetailLabel = (
  user: SecurityEventRecord['actor_user'] | SecurityEventRecord['target_user'],
  userId?: string | null,
  fallbackMasked?: string | null
) => {
  const label = userPrimaryLabel(user, userId);
  if ((!userId || label === 'n/a') && fallbackMasked) {
    return fallbackMasked;
  }
  if (!userId || label === userId || label === compactId(userId)) {
    return userId ?? label;
  }
  return `${label} (${userId})`;
};

const formatJson = (value: unknown) => {
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
};

const badgeToneForSeverity = (severity: SecurityEventSeverity) => {
  if (severity === 'critical') return 'critical';
  if (severity === 'error') return 'error';
  if (severity === 'warning') return 'warning';
  return 'default';
};

const badgeToneForOutcome = (outcome: SecurityEventOutcome) => {
  if (outcome === 'success') return 'success';
  if (outcome === 'blocked') return 'warning';
  return 'error';
};

type PagerProps = {
  page: number;
  pageSize: number;
  totalCount: number | null;
  rowCount: number;
  loading: boolean;
  onPageChange: (nextPage: number) => void;
};

const Pager: React.FC<PagerProps> = ({ page, pageSize, totalCount, rowCount, loading, onPageChange }) => {
  const totalPages = totalCount ? Math.max(1, Math.ceil(totalCount / pageSize)) : null;
  const from = totalCount && totalCount > 0 ? page * pageSize + 1 : 0;
  const to = totalCount && totalCount > 0 ? Math.min(totalCount, page * pageSize + rowCount) : rowCount;

  return (
    <PaginationBar>
      <ToolbarMeta>
        {totalCount != null
          ? `Showing ${from.toLocaleString()}-${to.toLocaleString()} of ${totalCount.toLocaleString()}`
          : loading
            ? 'Loading rows...'
            : `${rowCount.toLocaleString()} row${rowCount === 1 ? '' : 's'}`}
      </ToolbarMeta>
      <ToolbarCluster>
        <GhostButton type="button" disabled={loading || page <= 0} onClick={() => onPageChange(page - 1)}>
          Previous
        </GhostButton>
        <ToolbarMeta>{totalPages ? `Page ${page + 1} of ${totalPages}` : `Page ${page + 1}`}</ToolbarMeta>
        <GhostButton
          type="button"
          disabled={loading || rowCount < pageSize || (totalPages != null && page + 1 >= totalPages)}
          onClick={() => onPageChange(page + 1)}
        >
          Next
        </GhostButton>
      </ToolbarCluster>
    </PaginationBar>
  );
};

export const SuperAdminSecurityTab: React.FC = () => {
  const [summary, setSummary] = useState<SecuritySummary | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryError, setSummaryError] = useState<string | null>(null);

  const [eventRows, setEventRows] = useState<SecurityEventRecord[]>([]);
  const [eventLoading, setEventLoading] = useState(false);
  const [eventError, setEventError] = useState<string | null>(null);
  const [eventPage, setEventPage] = useState(0);
  const [eventPageSize, setEventPageSize] = useState(25);
  const [eventTotalCount, setEventTotalCount] = useState<number | null>(null);
  const [familyFilter, setFamilyFilter] = useState<'all' | SecurityEventFamily>('all');
  const [severityFilter, setSeverityFilter] = useState<'all' | SecurityEventSeverity>('all');
  const [outcomeFilter, setOutcomeFilter] = useState<'all' | SecurityEventOutcome>('all');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [selectedEvent, setSelectedEvent] = useState<SecurityEventRecord | null>(null);

  const [alerts, setAlerts] = useState<SecurityAlertRecord[]>([]);
  const [alertRuns, setAlertRuns] = useState<SecurityAlertRunRecord[]>([]);
  const [alertLoading, setAlertLoading] = useState(false);
  const [alertError, setAlertError] = useState<string | null>(null);
  const [alertPage, setAlertPage] = useState(0);
  const [alertPageSize, setAlertPageSize] = useState(25);
  const [alertTotalCount, setAlertTotalCount] = useState<number | null>(null);
  const [alertStatusFilter, setAlertStatusFilter] = useState<'all' | 'open' | 'acknowledged' | 'resolved'>('all');
  const [alertSeverityFilter, setAlertSeverityFilter] = useState<'all' | SecurityEventSeverity>('all');
  const [feedback, setFeedback] = useState<FeedbackState>(null);

  const loadSummary = useCallback(async () => {
    setSummaryLoading(true);
    setSummaryError(null);
    const result = await securityMonitorService.getSecuritySummary();
    if (!result.success || !result.data) {
      setSummaryError(result.message ?? 'Failed to load security summary.');
      setSummaryLoading(false);
      return;
    }
    setSummary(result.data);
    setSummaryLoading(false);
  }, []);

  const loadEvents = useCallback(async () => {
    setEventLoading(true);
    setEventError(null);
    const result = await securityMonitorService.getSecurityEventsPage(eventPageSize, eventPage * eventPageSize, {
      family: familyFilter === 'all' ? undefined : familyFilter,
      severity: severityFilter === 'all' ? undefined : severityFilter,
      outcome: outcomeFilter === 'all' ? undefined : outcomeFilter,
      search: search || undefined,
    });
    if (!result.success || !result.data) {
      setEventRows([]);
      setEventError(result.message ?? 'Failed to load security events.');
      setEventTotalCount(result.count ?? null);
      setEventLoading(false);
      return;
    }
    setEventRows(result.data);
    setEventTotalCount(result.count ?? null);
    setEventLoading(false);
  }, [eventPage, eventPageSize, familyFilter, outcomeFilter, search, severityFilter]);

  const loadAlerts = useCallback(async () => {
    setAlertLoading(true);
    setAlertError(null);
    const [alertsResult, runsResult] = await Promise.all([
      securityMonitorService.getSecurityAlertsPage(alertPageSize, alertPage * alertPageSize, {
        status: alertStatusFilter === 'all' ? undefined : alertStatusFilter,
        severity: alertSeverityFilter === 'all' ? undefined : alertSeverityFilter,
      }),
      securityMonitorService.getRecentSecurityAlertRuns(),
    ]);

    if (!alertsResult.success || !alertsResult.data) {
      setAlerts([]);
      setAlertTotalCount(alertsResult.count ?? null);
      setAlertError(alertsResult.message ?? 'Failed to load security alerts.');
    } else {
      setAlerts(alertsResult.data);
      setAlertTotalCount(alertsResult.count ?? null);
    }

    if (runsResult.success && runsResult.data) {
      setAlertRuns(runsResult.data);
    } else {
      setAlertRuns([]);
    }

    setAlertLoading(false);
  }, [alertPage, alertPageSize, alertSeverityFilter, alertStatusFilter]);

  const refreshAll = useCallback(async () => {
    setFeedback({ tone: 'info', text: 'Refreshing security telemetry...' });
    await Promise.all([loadSummary(), loadEvents(), loadAlerts()]);
    setFeedback({ tone: 'success', text: 'Security telemetry refreshed.' });
    window.setTimeout(() => setFeedback(null), 2500);
  }, [loadAlerts, loadEvents, loadSummary]);

  const handleAcknowledgeAlert = useCallback(
    async (id: string) => {
      const result = await securityMonitorService.acknowledgeAlert(id);
      if (!result.success) {
        setFeedback({ tone: 'error', text: result.message ?? 'Failed to acknowledge alert.' });
        return;
      }
      setFeedback({ tone: 'success', text: 'Security alert acknowledged.' });
      await loadAlerts();
    },
    [loadAlerts]
  );

  const handleResolveAlert = useCallback(
    async (id: string) => {
      const result = await securityMonitorService.resolveAlert(id);
      if (!result.success) {
        setFeedback({ tone: 'error', text: result.message ?? 'Failed to resolve alert.' });
        return;
      }
      setFeedback({ tone: 'success', text: 'Security alert resolved.' });
      await loadAlerts();
    },
    [loadAlerts]
  );

  useEffect(() => {
    void loadSummary();
  }, [loadSummary]);

  useEffect(() => {
    void loadEvents();
  }, [loadEvents]);

  useEffect(() => {
    void loadAlerts();
  }, [loadAlerts]);

  const eventStats = useMemo(() => {
    const failed = eventRows.filter((event) => event.outcome === 'failure').length;
    const blocked = eventRows.filter((event) => event.outcome === 'blocked').length;
    const critical = eventRows.filter((event) => event.severity === 'critical').length;
    return { total: eventRows.length, failed, blocked, critical };
  }, [eventRows]);

  return (
    <>
      <Card>
        <CardTitle>Security</CardTitle>
        <CardSubtitle>
          Detection-first cyber monitoring for suspicious auth behavior, high-risk admin activity, and automated
          security alerts.
        </CardSubtitle>
        <Stack>
          <SubCard>
            <HeaderRow>
              <div>
                <SubTitle>Overview</SubTitle>
                <SubTitleDesc>Recent security signals from auth flows and privileged admin actions.</SubTitleDesc>
              </div>
              <PrimaryButton disabled={summaryLoading || eventLoading || alertLoading} onClick={() => void refreshAll()}>
                {summaryLoading || eventLoading || alertLoading ? 'Refreshing...' : 'Refresh all'}
              </PrimaryButton>
            </HeaderRow>
            {feedback ? <Feedback $tone={feedback.tone}>{feedback.text}</Feedback> : null}
            {summaryError ? <Feedback $tone="error">{summaryError}</Feedback> : null}
            {summary ? (
              <SummaryGrid>
                <SummaryTile>
                  <MetricLabel>Failed sign-ins (24h)</MetricLabel>
                  <MetricValue $bad={summary.failedSignIns24h > 0}>{summary.failedSignIns24h}</MetricValue>
                </SummaryTile>
                <SummaryTile>
                  <MetricLabel>Recovery failures (24h)</MetricLabel>
                  <MetricValue $bad={summary.recoveryFailures24h > 0}>{summary.recoveryFailures24h}</MetricValue>
                </SummaryTile>
                <SummaryTile>
                  <MetricLabel>Privileged actions (24h)</MetricLabel>
                  <MetricValue>{summary.privilegedActions24h}</MetricValue>
                </SummaryTile>
                <SummaryTile>
                  <MetricLabel>Open critical alerts</MetricLabel>
                  <MetricValue $bad={summary.openCriticalAlerts > 0}>{summary.openCriticalAlerts}</MetricValue>
                  <SmallText>
                    Last event: {summary.lastEventAt ? new Date(summary.lastEventAt).toLocaleString() : 'n/a'}
                  </SmallText>
                </SummaryTile>
              </SummaryGrid>
            ) : summaryLoading ? (
              <SmallText>Loading security summary...</SmallText>
            ) : (
              <EmptyState>No security summary available yet.</EmptyState>
            )}
          </SubCard>

          <SubCard>
            <HeaderRow>
              <div>
                <SubTitle>Suspicious Activity Feed</SubTitle>
                <SubTitleDesc>Server-paged security events with severity, outcome, and request context.</SubTitleDesc>
              </div>
            </HeaderRow>
            <Toolbar>
              <ToolbarCluster>
                <PrimaryButton disabled={eventLoading} onClick={() => void loadEvents()}>
                  {eventLoading ? 'Refreshing...' : 'Refresh'}
                </PrimaryButton>
                <Select
                  value={familyFilter}
                  onChange={(event) => {
                    setEventPage(0);
                    setFamilyFilter(event.target.value as 'all' | SecurityEventFamily);
                  }}
                >
                  <option value="all">All families</option>
                  <option value="auth">Auth</option>
                  <option value="account_recovery">Account recovery</option>
                  <option value="admin_action">Admin action</option>
                  <option value="privilege_change">Privilege change</option>
                </Select>
                <Select
                  value={severityFilter}
                  onChange={(event) => {
                    setEventPage(0);
                    setSeverityFilter(event.target.value as 'all' | SecurityEventSeverity);
                  }}
                >
                  <option value="all">All severities</option>
                  <option value="critical">Critical</option>
                  <option value="error">Error</option>
                  <option value="warning">Warning</option>
                  <option value="info">Info</option>
                </Select>
                <Select
                  value={outcomeFilter}
                  onChange={(event) => {
                    setEventPage(0);
                    setOutcomeFilter(event.target.value as 'all' | SecurityEventOutcome);
                  }}
                >
                  <option value="all">All outcomes</option>
                  <option value="failure">Failure</option>
                  <option value="blocked">Blocked</option>
                  <option value="success">Success</option>
                </Select>
                <SearchInput
                  value={searchInput}
                  onChange={(event) => setSearchInput(event.target.value)}
                  placeholder="Search event, source, or session"
                />
                <PrimaryButton
                  disabled={eventLoading}
                  onClick={() => {
                    setEventPage(0);
                    setSearch(searchInput.trim());
                  }}
                >
                  Search
                </PrimaryButton>
              </ToolbarCluster>
              <ToolbarCluster>
                <ToolbarMeta>Rows per page</ToolbarMeta>
                <Select
                  value={String(eventPageSize)}
                  onChange={(event) => {
                    setEventPage(0);
                    setEventPageSize(Number(event.target.value));
                  }}
                >
                  {[25, 50, 100].map((size) => (
                    <option key={size} value={size}>
                      {size} / page
                    </option>
                  ))}
                </Select>
              </ToolbarCluster>
            </Toolbar>
            {eventError ? <Feedback $tone="error">{eventError}</Feedback> : null}
            <SummaryGrid>
              <SummaryTile>
                <MetricLabel>Page total</MetricLabel>
                <MetricValue>{eventStats.total}</MetricValue>
              </SummaryTile>
              <SummaryTile>
                <MetricLabel>Failures</MetricLabel>
                <MetricValue $bad={eventStats.failed > 0}>{eventStats.failed}</MetricValue>
              </SummaryTile>
              <SummaryTile>
                <MetricLabel>Blocked</MetricLabel>
                <MetricValue $bad={eventStats.blocked > 0}>{eventStats.blocked}</MetricValue>
              </SummaryTile>
              <SummaryTile>
                <MetricLabel>Critical</MetricLabel>
                <MetricValue $bad={eventStats.critical > 0}>{eventStats.critical}</MetricValue>
              </SummaryTile>
            </SummaryGrid>
            {eventRows.length === 0 && !eventLoading ? (
              <EmptyState>No security events recorded for the current filters.</EmptyState>
            ) : (
              <TableShell>
                <TableScroll>
                  <Table>
                    <thead>
                      <tr>
                        <th>When</th>
                        <th>Family</th>
                        <th>Event</th>
                        <th>Severity</th>
                        <th>Outcome</th>
                        <th>Source</th>
                        <th>Actor</th>
                        <th>Target</th>
                        <th>Details</th>
                      </tr>
                    </thead>
                    <tbody>
                      {eventRows.map((event) => (
                        <tr key={event.id}>
                          <td>{new Date(event.created_at).toLocaleString()}</td>
                          <td>{event.event_family}</td>
                          <td>{event.event_type}</td>
                          <td>
                            <Badge $tone={badgeToneForSeverity(event.severity)}>{event.severity}</Badge>
                          </td>
                          <td>
                            <Badge $tone={badgeToneForOutcome(event.outcome)}>{event.outcome}</Badge>
                          </td>
                          <td>{event.source_name}</td>
                          <td>{renderActorSummary(event)}</td>
                          <td>{renderUserSummary(event.target_user, event.target_user_id)}</td>
                          <td>
                            <DetailButton type="button" onClick={() => setSelectedEvent(event)}>
                              {event.path || event.session_id ? 'View detail' : 'Open'}
                            </DetailButton>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </TableScroll>
                <Pager
                  loading={eventLoading}
                  page={eventPage}
                  pageSize={eventPageSize}
                  totalCount={eventTotalCount}
                  rowCount={eventRows.length}
                  onPageChange={setEventPage}
                />
              </TableShell>
            )}
          </SubCard>

          <SubCard>
            <HeaderRow>
              <div>
                <SubTitle>Security Alerts</SubTitle>
                <SubTitleDesc>Detector output written into the shared monitoring alert pipeline.</SubTitleDesc>
              </div>
            </HeaderRow>
            <Toolbar>
              <ToolbarCluster>
                <PrimaryButton disabled={alertLoading} onClick={() => void loadAlerts()}>
                  {alertLoading ? 'Refreshing...' : 'Refresh'}
                </PrimaryButton>
                <Select
                  value={alertStatusFilter}
                  onChange={(event) => {
                    setAlertPage(0);
                    setAlertStatusFilter(event.target.value as typeof alertStatusFilter);
                  }}
                >
                  <option value="all">All statuses</option>
                  <option value="open">Open</option>
                  <option value="acknowledged">Acknowledged</option>
                  <option value="resolved">Resolved</option>
                </Select>
                <Select
                  value={alertSeverityFilter}
                  onChange={(event) => {
                    setAlertPage(0);
                    setAlertSeverityFilter(event.target.value as typeof alertSeverityFilter);
                  }}
                >
                  <option value="all">All severities</option>
                  <option value="critical">Critical</option>
                  <option value="error">Error</option>
                  <option value="warning">Warning</option>
                  <option value="info">Info</option>
                </Select>
              </ToolbarCluster>
              <ToolbarCluster>
                <ToolbarMeta>Rows per page</ToolbarMeta>
                <Select
                  value={String(alertPageSize)}
                  onChange={(event) => {
                    setAlertPage(0);
                    setAlertPageSize(Number(event.target.value));
                  }}
                >
                  {[25, 50, 100].map((size) => (
                    <option key={size} value={size}>
                      {size} / page
                    </option>
                  ))}
                </Select>
              </ToolbarCluster>
            </Toolbar>
            {alertError ? <Feedback $tone="error">{alertError}</Feedback> : null}
            {alerts.length === 0 && !alertLoading ? (
              <EmptyState>No security alerts recorded yet.</EmptyState>
            ) : (
              <TableShell>
                <TableScroll>
                  <Table>
                    <thead>
                      <tr>
                        <th>When</th>
                        <th>Status</th>
                        <th>Severity</th>
                        <th>Source</th>
                        <th>Title</th>
                        <th>Occurrences</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {alerts.map((alert) => (
                        <tr key={alert.id}>
                          <td>{new Date(alert.created_at).toLocaleString()}</td>
                          <td>{alert.status}</td>
                          <td>
                            <Badge $tone={badgeToneForSeverity(alert.severity)}>{alert.severity}</Badge>
                          </td>
                          <td>{alert.source}</td>
                          <td>{alert.title}</td>
                          <td>{alert.occurrence_count}</td>
                          <td>
                            {alert.status === 'open' ? (
                              <ToolbarCluster>
                                <GhostButton type="button" onClick={() => void handleAcknowledgeAlert(alert.id)}>
                                  Acknowledge
                                </GhostButton>
                                <PrimaryButton type="button" onClick={() => void handleResolveAlert(alert.id)}>
                                  Resolve
                                </PrimaryButton>
                              </ToolbarCluster>
                            ) : (
                              'n/a'
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </TableScroll>
                <Pager
                  loading={alertLoading}
                  page={alertPage}
                  pageSize={alertPageSize}
                  totalCount={alertTotalCount}
                  rowCount={alerts.length}
                  onPageChange={setAlertPage}
                />
              </TableShell>
            )}
            {alertRuns.length > 0 ? (
              <TableShell>
                <TableScroll>
                  <Table>
                    <thead>
                      <tr>
                        <th>When</th>
                        <th>Detector</th>
                        <th>Outcome</th>
                        <th>Created</th>
                        <th>Updated</th>
                      </tr>
                    </thead>
                    <tbody>
                      {alertRuns.map((run) => (
                        <tr key={run.id}>
                          <td>{new Date(run.created_at).toLocaleString()}</td>
                          <td>{run.detector_name}</td>
                          <td>{run.outcome}</td>
                          <td>{run.alerts_created}</td>
                          <td>{run.alerts_updated}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </TableScroll>
              </TableShell>
            ) : null}
          </SubCard>
        </Stack>
      </Card>

      {selectedEvent ? (
        <ModalOverlay onClick={() => setSelectedEvent(null)}>
          <ModalCard onClick={(event) => event.stopPropagation()}>
            <HeaderRow>
              <div>
                <CardTitle>Security Event Detail</CardTitle>
                <CardSubtitle>{new Date(selectedEvent.created_at).toLocaleString()}</CardSubtitle>
              </div>
              <PrimaryButton type="button" onClick={() => setSelectedEvent(null)}>
                Close
              </PrimaryButton>
            </HeaderRow>
            <Stack>
              <DetailGrid>
                <DetailField>
                  <DetailLabel>Event Family</DetailLabel>
                  <span>{selectedEvent.event_family}</span>
                </DetailField>
                <DetailField>
                  <DetailLabel>Event Type</DetailLabel>
                  <span>{selectedEvent.event_type}</span>
                </DetailField>
                <DetailField>
                  <DetailLabel>Severity</DetailLabel>
                  <span>{selectedEvent.severity}</span>
                </DetailField>
                <DetailField>
                  <DetailLabel>Outcome</DetailLabel>
                  <span>{selectedEvent.outcome}</span>
                </DetailField>
                <DetailField>
                  <DetailLabel>Source</DetailLabel>
                  <span>{selectedEvent.source_name}</span>
                </DetailField>
                <DetailField>
                  <DetailLabel>Path</DetailLabel>
                  <span>{selectedEvent.path ?? 'n/a'}</span>
                </DetailField>
                <DetailField>
                  <DetailLabel>Actor User</DetailLabel>
                  <span>
                    {userDetailLabel(
                      selectedEvent.actor_user,
                      selectedEvent.actor_user_id,
                      selectedEvent.identifier_masked
                    )}
                  </span>
                </DetailField>
                <DetailField>
                  <DetailLabel>Actor Email</DetailLabel>
                  <span>{normalizeContactValue(selectedEvent.actor_user?.email) ?? 'n/a'}</span>
                </DetailField>
                <DetailField>
                  <DetailLabel>Actor Phone</DetailLabel>
                  <span>{normalizeContactValue(selectedEvent.actor_user?.phone) ?? 'n/a'}</span>
                </DetailField>
                <DetailField>
                  <DetailLabel>Attempted Identifier</DetailLabel>
                  <span>{normalizeContactValue(selectedEvent.identifier_masked) ?? 'n/a'}</span>
                </DetailField>
                <DetailField>
                  <DetailLabel>Target User</DetailLabel>
                  <span>{userDetailLabel(selectedEvent.target_user, selectedEvent.target_user_id)}</span>
                </DetailField>
                <DetailField>
                  <DetailLabel>Target Email</DetailLabel>
                  <span>{normalizeContactValue(selectedEvent.target_user?.email) ?? 'n/a'}</span>
                </DetailField>
                <DetailField>
                  <DetailLabel>Target Phone</DetailLabel>
                  <span>{normalizeContactValue(selectedEvent.target_user?.phone) ?? 'n/a'}</span>
                </DetailField>
                <DetailField>
                  <DetailLabel>Session ID</DetailLabel>
                  <span>{selectedEvent.session_id ?? 'n/a'}</span>
                </DetailField>
                <DetailField>
                  <DetailLabel>Request Method</DetailLabel>
                  <span>{selectedEvent.request_method ?? 'n/a'}</span>
                </DetailField>
                <DetailField>
                  <DetailLabel>IP Hash</DetailLabel>
                  <span>{selectedEvent.ip_hash ?? 'n/a'}</span>
                </DetailField>
                <DetailField>
                  <DetailLabel>Identifier Hash</DetailLabel>
                  <span>{selectedEvent.identifier_hash ?? 'n/a'}</span>
                </DetailField>
              </DetailGrid>
              <div>
                <DetailLabel>Metadata</DetailLabel>
                <CodeBlock>{formatJson(selectedEvent.metadata ?? {})}</CodeBlock>
              </div>
            </Stack>
          </ModalCard>
        </ModalOverlay>
      ) : null}
    </>
  );
};
